import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale, FLOATING_ACTION_BAR_CLEARANCE } from '@/theme';
import { mq } from '@/theme/scalingTokens';

/**
 * Shared surface dimensions used by the booking flow surfaces (hero banner,
 * form outer card, inline summary bar, review card) so all step surfaces
 * align horizontally with the hero banner across every step (FR-016).
 */
export const BOOKING_SURFACE_MAX_WIDTH = '75rem';
export const BOOKING_SURFACE_PADDING_X = tokens.spacingHorizontalL;
export const BOOKING_SURFACE_PADDING_X_NARROW = tokens.spacingHorizontalM;

export const useBookingFormStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    width: '100%',
    maxWidth: '85rem',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalL,
    [mq.smallDesktop]: {
      paddingInline: tokens.spacingHorizontalM,
    },

    [mq.mobile]: {
      paddingInline: 0,
    },
  },
  /** Figma 2026 wizard chrome: page max-width (shared with hero banner for FR-016). */
  rootFigma: {
    width: '100%',
    maxWidth: BOOKING_SURFACE_MAX_WIDTH,
    marginInline: 'auto',
  },
  sidebar: {
    position: 'sticky',
    top: '1.5rem',
    alignSelf: 'flex-start',
    [mq.smallDesktop]: {
      position: 'static',
      alignSelf: 'stretch',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    minWidth: 0,
    maxWidth: '100%',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
  },
  stepTitleSection: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '0.25rem',
    marginBottom: '0.5rem',
  },
  stepTitle: {
    fontSize: fontScale.title,
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.23em',
    margin: 0,
  },
  /** Figma 2026 step title (matches the page H1 outside the form card). */
  stepTitleFigma: {
    fontSize: fontScale.title,
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.2em',
    margin: 0,
  },
  stepSubtitle: {
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground3,
    lineHeight: '1.43em',
    margin: 0,
  },
  formOuterCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('1rem'),
    ...shorthands.padding('1.5rem'),
    boxShadow: tokens.shadow4,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    minHeight: 0,
    [mq.tablet]: {
      ...shorthands.padding('1rem'),
      ...shorthands.borderRadius('0.75rem'),
    },
    [mq.mobile]: {
      ...shorthands.padding('0.75rem'),
      ...shorthands.borderRadius('0.5rem'),
    },
  },
  /** Figma 2026 outer card: white surface with neutral border + 8 px radius. */
  formOuterCardFigma: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('0.5rem'),
    boxShadow: 'none',
  },
  /** Figma 2026 summary bar (under step title): thin pill row of context chips. */
  summaryBarFigma: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('0.5rem'),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalL),
  },
  formInnerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('0.5rem'),
    boxShadow: tokens.shadow2,
    overflow: 'visible',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
  },
  tipBar: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: '0.5rem',
    ...shorthands.padding('0.75rem', '1rem'),
    backgroundColor: tokens.colorPaletteBlueBackground2,
    ...shorthands.borderRadius('0.25rem'),
    ...shorthands.border('0.0625rem', 'solid', tokens.colorPaletteBlueBorderActive),
    fontSize: fontScale.body,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.43em',
    marginTop: '1rem',
    [mq.mobile]: {
      ...shorthands.padding('0.625rem', '0.75rem'),
    },
  },
  tipIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
    marginTop: '0.125rem',
  },
  policyBanner: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteYellowBorder1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    marginTop: tokens.spacingVerticalM,
  },
  policyLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  navigation: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '1.5rem',
    ...shorthands.padding('1rem', '0', '0', '0'),
    ...shorthands.borderTop('0.0625rem', 'solid', tokens.colorNeutralStroke2),
    flexWrap: 'wrap',
    [mq.tablet]: {
      gap: '0.5rem',
      marginTop: '1rem',
    },
    [mq.smallTablet]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
      gap: tokens.spacingVerticalS,
    },
    [mq.mobile]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
    },
  },
  navLeft: {
    marginRight: 'auto',
    [mq.mobile]: {
      marginRight: '0',
      marginBottom: '0.25rem',
    },
  },
  /**
   * Compact floating action banner for the Available Spaces step.
   * Mirrors the tinted/bordered surface of the home `BookAnotherCard` so the
   * lower floating bars across the employee module share a single visual
   * language. Back sits on the far left; Save-as-draft + Next on the far right.
   */
  spaceStepActionBanner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalM,
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('0.5rem'),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    [mq.smallTablet]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
      rowGap: tokens.spacingVerticalS,
      '& > button': {
        width: '100%',
        minWidth: 0,
      },
    },
    [mq.mobile]: {
      ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    },
  },
  spaceStepActionRight: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    [mq.smallTablet]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
      width: '100%',
      rowGap: tokens.spacingVerticalS,
      '& > button': {
        width: '100%',
        minWidth: 0,
      },
    },
  },
  /**
   * Bottom clearance for the Available Spaces step content. The Back/Save/Next
   * bar is pinned (FloatingActionBar) above the footer, so the space cards grid
   * reserves room beneath the last card to let its room-setup details scroll
   * fully clear of the bar instead of being overlapped when scrolling stops.
   */
  spaceStepScrollClearance: {
    paddingBottom: FLOATING_ACTION_BAR_CLEARANCE,
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    marginTop: 'auto',
    backgroundColor: tokens.colorBrandBackground2,
    borderTopColor: tokens.colorNeutralStroke1,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderBottomLeftRadius: '0.5rem',
    borderBottomRightRadius: '0.5rem',
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalM,
    [mq.smallTablet]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    },
  },
  cardFooterLeft: {
    display: 'flex',
    alignItems: 'center',
    // Pushes the right group to the far edge; collapses when Back isn't
    // rendered (first step) so flex-end alone keeps the right group right-aligned.
    marginRight: 'auto',
    ':empty': {
      display: 'none',
    },
    [mq.smallTablet]: {
      marginRight: 0,
      width: '100%',
      '& > button': {
        width: '100%',
        minWidth: 0,
      },
    },
  },
  cardFooterRight: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
    [mq.smallTablet]: {
      flexDirection: 'column-reverse',
      alignItems: 'stretch',
      width: '100%',
      rowGap: tokens.spacingVerticalS,
      '& > button': {
        width: '100%',
        minWidth: 0,
      },
    },
  },
  emptyMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding(tokens.spacingVerticalXXL),
    color: tokens.colorNeutralForeground2,
    fontSize: fontScale.subtitle,
  },
  errorBanner: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    ...shorthands.padding('0.75rem', '1rem'),
    ...shorthands.borderRadius('0.25rem'),
    fontSize: fontScale.body,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
    [mq.mobile]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '0.5rem',
    },
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: '1rem',
    ...shorthands.padding('0.75rem', '1rem'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('0.5rem'),
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground2,
    [mq.tablet]: {
      gap: '0.5rem 1rem',
      ...shorthands.padding('0.625rem', '0.75rem'),
    },
    [mq.mobile]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '0.375rem',
      '& > button:last-child': {
        marginLeft: 0,
      },
    },
  },
  summaryItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  summaryIcon: {
    width: '1rem',
    height: '1rem',
    flexShrink: 0,
    marginRight: '0.25rem',
    objectFit: 'contain',
  },
  summaryLabel: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  inlineEditContainer: {
    marginBottom: '0.5rem',
  },
  resultsCount: {
    fontSize: fontScale.caption,
    color: tokens.colorNeutralForeground2,
    marginBottom: '0.5rem',
  },
});
